import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Models } from '@ibexfusion/models';
import { ActivatedRoute, Router } from '@angular/router';
import { DynamicContentEntranceService } from '@services/dynamic-content/dynamic-content-entrance.service';
import { DynamicContentService } from '@services/dynamic-content/dynamic-content.service';
import { PageAction } from '../../components/page-actions/page-actions.component';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { DataTableComponent } from '../../components/data-table/data-table.component';
import { TranslateService } from '@services/translate/translate.service';
import { StorageService } from '@services/storage/storage.service';
import { PrivilegeGuard } from '../../guards/privilege.guard';
import { PipeImportService } from '@services/pipe-imports/pipe-import.service';
import { MessengerService } from '@services/messenger/messenger.service';

/* these Services are injected by dynamicContentEntranceService */
import { PackageService } from '@services/packages/package.service';
import { PackageInstalledAppsService } from '@services/packages/package-installed-apps.service';

import * as _ from 'lodash';
import moment from 'moment';
import { QueService } from '@services/que/que.service';
import { DynamicContentActionComponent } from './dynamic-content-action/dynamic-content-action.component';

@Component({
    selector: 'app-dynamic-content',
    templateUrl: './dynamic-content.component.html',
    styleUrls: ['./dynamic-content.component.scss'],
    providers: [DynamicContentEntranceService, DynamicContentService, PackageService, PackageInstalledAppsService, PipeImportService, QueService],
    standalone: false
})
export class DynamicContentComponent implements OnInit, OnDestroy {

  @ViewChildren(DataTableComponent) table: QueryList<DataTableComponent>;
  public pageActions: PageAction[];
  public subtitle: string;
  public dynamicContentEntrances: any[] = [];
  public packageIds;
  public activeType = '';
  public isLoading = true;
  public loading: boolean;
  public data: any = [];
  public categoryData = [];
  public tabIndex = 0;
  public isReadOnly = false;
  public location: string;
  public activeSubTab = 'person';
  public pipes: Models.PipeImport[];
  public showReloadPipeData = false;
  public showInitPipeData = false;
  public showOrder = false;
  private dynamicContentQuery;
  private currentPipe;
  private children: any = {};
  private translations: any = {};
  private selectedTab: any;
  private currentLanguage = 'de' // currently only german
  private subscriptions = [];
  private apps: Models.PackageInstalledApp[];
  public app: Models.PackageInstalledApp;
  private packages: Models.Package[];
  private pak: Models.Package;
  public globalEntries: any[];
  public showHistory = false;

  constructor(
    public entranceService: DynamicContentEntranceService, public contentService: DynamicContentService, private appService: PackageInstalledAppsService, private router: Router,
    private route: ActivatedRoute, private snackbar: SnackbarService, private ts: TranslateService, private storage: StorageService, private que: QueService,
    private pipeImportService: PipeImportService, private messengerService: MessengerService, private packageService: PackageService, private priv: PrivilegeGuard
  ) {
  }

  async init() {
    if (!this.route.snapshot.params['packageId']) {
      this.snackbar.open('Sorry, wrong routing');
      this.router.navigate(['dynamic-content-entrances']);
      return;
    }
    this.selectedTab = this.route.snapshot.queryParams['tab'];
    this.packageIds = this.route.snapshot.params['packageId'].split(',');
    this.packages = (await this.packageService.find(0, 100, { _id: { $in: this.packageIds } })).data || [];
    if (this.packageIds) {
      window['forcePackage'] = this.packageIds[0];
    }
    this.isReadOnly = !this.priv.checkIfPrivilege('write', 'dynamic-contents'); // demo-organisation //
    this.location = this.router.url;
    this.subscriptions.push(this.messengerService.$activeContents.subscribe(contents => {
      this.initContent(contents);
    }));
    this.subscriptions.push(this.messengerService.$superadminPrivileges.subscribe(privileges => {
      this.showHistory = !!(privileges || []).find(p => p.value === 'dynamic-content-historys');
    }));
  }

  async initContent(contents) {
    if (!contents) {
      return;
    }
    this.isLoading = true;
    this.initPipeImport(this.messengerService.getOrganisation()?.OrganisationID);
    this.createPageActions();
    if (!this.messengerService.getOrganisation()) {
      return;
    }
    const query: any = {};
    this.pak = (await this.packageService.find(0, 1, { _id: { $in: this.packageIds } }))?.data?.[0];
    if (!!contents?.length) {
      query['_id'] = { $in: contents.map(el => el['dynamicContentEntrance_Id']) };
    } else {
      const apps = (await this.appService.find(0, this.pak?.installedAppIds?.length, { _id: { $in: this.packages.map(pak => pak.installedAppIds).flat() } }))?.data;
      this.apps = apps.filter(app => this.pak.compositionIds.includes(app.compositionId));
      query['_id'] = { $in: this.apps.map(app => app.dynamicContentEntranceId).filter(el => !!el) };
    }
    this.getDynamicContentEntrances(query);
  }

  async ngOnInit() {
    this.init();
  }

  ngOnDestroy() {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }

  public async getDynamicContentEntrances(query) {
    let result;
    try {
      result = await this.entranceService.findAssigned(0, 100, query, query.OwnerOrganisationID ? { packageIds: this.packageIds } : null);
    } catch (err) {
      console.warn(err);
    }
    this.dynamicContentEntrances = result?.entrances || [];
    this.dynamicContentEntrances.map(entrance => {
      entrance['translations'].map(translation => {
        if (translation.language === this.currentLanguage) {
          entrance.title = translation.appTitle
        }
      });
    });
    if (this.pak) {
      this.apps = (await this.appService.find(0, this.pak?.installedAppIds?.length, { _id: { $in: this.pak?.installedAppIds } }))?.data;
      let ids = this.pak.ibexTouch?.enabled ? this.pak.installedAppIds : (this.pak.ibexApp?.enabled ? this.pak.installedMobileAppIds : this.pak.installedTabletAppIds);
      if (!ids) {
        ids = this.pak?.[['installedAppIds', 'installedWidgetIds', 'installedMobileAppIds', 'installedMobileWidgetIds', 'installedTabletAppIds', 'installedTabletWidgetIds'].find(el => !!this.pak?.[el])];
      }

      const order = this.apps.sort((app1, app2) => {
        return ids.indexOf(app1._id) - ids.indexOf(app2._id);
      }).map(el => el.dynamicContentEntranceId).filter(el => !!el);
      this.dynamicContentEntrances = this.dynamicContentEntrances.sort((entrance1, entrance2) => {
        return order.indexOf(entrance1._id) - order.indexOf(entrance2._id);
      });
    }
    if (this.selectedTab) {
      const index = this.dynamicContentEntrances.findIndex(el => el._id === this.selectedTab);
      if (index !== -1) {
        await this.tabChange({ index });
      }
    }
    const storageData = await this.storage.getData('data-table-filter-' + this.location);
    let secondIndex = 0;
    if (storageData?.tabIndex && storageData.tabIndex <= this.dynamicContentEntrances.length - 1) {
      secondIndex = storageData.tabIndex;
    }
    if (!this.selectedTab) {
      await this.tabChange({ index: secondIndex });
    }
    this.showOrder = this.dynamicContentEntrances?.[this.tabIndex]?.type === 'person' && !!(this.data?.live || this.data || []).find(el => !!el?.data?.content?.order);
    this.isLoading = false;
  }

  public checkValid(item): any {
    return DynamicContentActionComponent.checkValid(item);
  }

  public info(data) {
    this.data[0].children = [];
    this.data[0].children.push({ data: data });
  }

  async openTab(index) {
    if (this.loading) {
      return;
    }
    this.selectedTab = index;
    this.loading = true;
    this.data = [];
    await this.tabChange({ index: index });
  }

  public async tabChange(event) {
    this.tabIndex = event.index;
    const entrance = this.dynamicContentEntrances[this.tabIndex];
    if (!entrance) {
      this.activeType = 'information';
      this.activeSubTab = 'information';
      return;
    }
    this.activeType = entrance?.type;
    this.activeSubTab = entrance?.type;
    this.dynamicContentQuery = { entranceId: entrance?._id, withSubs: true };
    this.checkCurrentPipe(entrance?._id);
    this.storage.setData('data-table-filter-' + this.location, { tabIndex: this.tabIndex });
    await this.getContent();
    this.showOrder = this.dynamicContentEntrances?.[this.tabIndex]?.type === 'person' && !!(this.data?.live || this.data || []).find(el => !!el?.data?.content?.order);
  }

  public hasCategories(entrance: Models.DynamicContentEntrance): boolean {
    return (!entrance?.noCategories && ['person', 'activity', 'voucher'].includes(entrance?.type)) || !!entrance?.config?.hasCategories;
  }

  public async getContent() {
    this.loading = true;
    const entrance = await this.entranceService.get(this.dynamicContentEntrances[this.tabIndex]._id);
    const globalResult = await this.contentService.find(0, 1000, {
      global: true, type: this.activeType, organisationIds: this.messengerService.$activeOrganisation.getValue().OrganisationID
    });
    const globalResult2 = await this.contentService.find(0, 1000, { global: true, type: this.activeType, organisationIds: 'all' });
    globalResult.data = (globalResult?.data || []).concat(globalResult2?.data || []);
    this.globalEntries = (globalResult?.data || []).filter(e => !(entrance?.children || []).includes(e._id));
    let tree = [];
    this.categoryData = [];
    const categoryTreeItems = [];
    if (this.hasCategories(entrance) && entrance?.categories?.length) {
      const categories = await this.contentService.find(0, 1000, { _id: { $in: entrance.categories } });
      for (const category of categories.data) {
        const treeItem = await this.pushToTree(category, 0);
        treeItem.data.parent = entrance._id;
        categoryTreeItems.push(treeItem);
      }
      categoryTreeItems.sort((obj1, obj2) => {
        return obj1.data.order - obj2.data.order
      });
      this.categoryData = categoryTreeItems;
    }

    if (!this.dynamicContentQuery || !entrance?.children?.length) {
      this.data = [];
      this.loading = false;
      return;
    }
    const result = await this.contentService.find(0, 1000, this.dynamicContentQuery);
    if (result.total > 1000) {
      while (result.data.length !== result.total) {
        const subRes = await this.contentService.find(result.data.length, 1000, this.dynamicContentQuery);
        result.data = result.data.concat(subRes.data);
      }
    }
    for (let i = 0; i < result?.data.length; i++) {
      const copy = JSON.stringify(result?.data[i]);
      for (let j = 0; j < result.data[i]?.children?.length; j++) {
        if (typeof result.data[i]?.children[j] === 'string' || !(result.data[i]?.children[j] as any)?._id) {
          continue;
        }
        result.data[i].children[j] = (result.data[i]?.children[j] as any)._id?.toString();
      }
      if (JSON.stringify(result.data[i]) !== copy) {
        await this.contentService.update(result.data[i]._id, result.data[i]);
      }
    }
    if (!result || !result.data || !result.data.length) {
      this.data = tree;
      this.loading = false;
      return;
    }
    for (const child of result.children) {
      const item = new Models.DynamicContent();
      item.populateFromJSON(child);
      this.children[child._id] = item;
    }
    for (const translation of result.translations) {
      const item = new Models.DynamicContent();
      item.populateFromJSON(translation);
      this.translations[translation._id] = item;
    }
    for (let i = 0; i < result.data.length; i++) {
      const element = result.data[i];
      if (element?.content?.date?.indexOf?.('Invalid') > -1) {
        result.data.splice(i, 1);
        i--;
      }
    }
    this.app = this.apps.find(app => app.dynamicContentEntranceId === this.dynamicContentEntrances[this.tabIndex]._id);
    if (this.activeType === 'person') {
      result.data.forEach(entry => {
        entry.title = this.getPersonTitle(entry?.content);
      });
    }
    for (const element of result.data) {
      const level = 1;
      const treeItem = await this.pushToTree(element, level);
      treeItem.data.isActive = this.checkValid(treeItem.data);
      treeItem.data.parent = this.dynamicContentEntrances[this.tabIndex]._id;
      tree.push(treeItem);
    }
    if (['alphabet', 'restaurant'].includes(this.activeType)) {
      tree.sort((obj1, obj2) => {
        return obj1.data.title.localeCompare(obj2.data.title);
      });
    } else if (['event', 'gastronomy'].includes(this.activeType)) {
      if (this.activeType === 'gastronomy') {
        if (entrance?.config?.renderAsRelated && entrance?.config?.startLevel === 1) {
          tree.sort((obj1, obj2): number | any => {
            return new Date(obj2.data.content.date).getTime() - new Date(obj1.data.content.date).getTime();
          });
        } else {
          tree.forEach(el => {
            el?.children?.sort((obj1, obj2) => {
              return new Date(obj1?.data?.content?.date).getTime() - new Date(obj2?.data?.content?.date).getTime()
            });
          });
          tree.sort((obj1, obj2): number | any => {
            return new Date(obj1?.children?.[0]?.data?.content?.date).getTime() - new Date(obj2?.children?.[0]?.data?.content?.date).getTime()
          });
        }
      } else {
        tree.sort((obj1, obj2) => {
          const d1 = obj1.data?.content?.validity?.start || (obj1.data?.content?.dates?.startDate ? parseInt(moment(obj1.data?.content?.dates?.startDate).format('X'), 10) : null);
          const d2 = obj2.data?.content?.validity?.start || (obj2.data?.content?.dates?.startDate ? parseInt(moment(obj2.data?.content?.dates?.startDate).format('X'), 10) : null);
          return d1 && d2 ? d1 - d2 : (obj1?.data?.title?.localeCompare(obj2?.data?.title) || 0);
        });
        tree.sort((obj1, obj2) => {
          return obj1.data.published !== obj2.data.published ? (obj1.data.published ? -1 : 1) :
            ((this.checkValid(obj1.data) === this.checkValid(obj2.data)) ? 0 : (this.checkValid(obj1.data) ? (this.checkValid(obj1.data) ? -1 : 1) : null));
        });
      }
    } else if (this.activeType === 'person') {
      tree.sort((a, b) => {
        const rangDiff = ((v => v ? parseInt(v) : 1e15)(a.data?.content?.order) - (v => v ? parseInt(v) : 1e15)(b.data?.content?.order));
        return rangDiff || (this.getPersonTitle(a?.data?.content) || a.data.title || '').localeCompare(this.getPersonTitle(b?.data?.content) || b.data.title || '');
      });
    } else if (this.activeType === 'activity') {
      tree.sort((obj1, obj2): number | any => {
        return parseInt(this.calculateNextExecution(obj1.data).format('x'), 10) - parseInt(this.calculateNextExecution(obj2.data).format('x'), 10);
      });
    } else {
      tree.sort((obj1, obj2) => {
        return entrance.children.indexOf(obj1.data._id) - entrance.children.indexOf(obj2.data._id);
      });
    }
    if ((!['event', 'activity', 'gastronomy', 'restaurant', 'event', 'person', 'alphabet'].includes(this.activeType))) {
      tree = await this.fixOrder(tree, entrance, 'dynamicContentEntrance');
    }
    this.data = this.splitLiveArchive(tree, this.activeType);
    if (!!['event', 'activity', 'gastronomy', 'event'].includes(this.activeType)) {
      this.data.archive = this.data.archive?.reverse();
    }
    this.loading = false;
  }

  getPersonTitle({ firstname: first, lastname: last }) {
    return first && last ? this.app?.detail?.flipNames ? `${last} ${first}` : `${first} ${last}` : first || last || null;
  }

  async fixOrder(children, parent, parentType) {
    if (!this.dynamicContentEntrances?.[this.tabIndex]?.children?.length) {
      return children;
    }
    for (const child of (children || []).filter(c => !!c.children?.length)) {
      child.children = await this.fixOrder(child.children, child.data, 'dynamicContent');
    }
    const childrenCopy = JSON.stringify(children);
    if (children.map(child => child.data._id) !== children.sort((a, b) => this.getOrder(a) - this.getOrder(b)).map(child => child.data._id)) {
      children = children.sort((a, b) => this.getOrder(a) - this.getOrder(b));
      if (this[parentType === 'dynamicContentEntrance' ? 'entranceService' : 'contentService']?.patch && JSON.stringify(children) !== childrenCopy) {
        await this[parentType === 'dynamicContentEntrance' ? 'entranceService' : 'contentService'].patch(parent._id, { children: children.map(child => child.data._id) });
      }
    }
    return children;
  }

  private getOrder(element) {
    return (element.data.orderByPackage || {}).hasOwnProperty(this.packageIds[0]) ? element.data.orderByPackage[this.packageIds[0]] : element.data.order;
  }

  private splitLiveArchive(data, type) {
    const live = [];
    const archive = [];
    const today = moment().startOf('day');
    for (const element of data) {
      const addToList = (condition) => condition ? live.push(element) : archive.push(element);
      switch (type) {
        case 'gastronomy':
          const hasFutureChildren = element.children?.some(child => moment(child?.content?.date || child?.data?.content?.date).isSameOrAfter(today));
          const hasFutureDate = (element?.content?.date || element?.data?.content?.date) && moment(element?.content?.date || element?.data?.content?.date).isSameOrAfter(today);
          addToList((hasFutureChildren || hasFutureDate || !element.children?.length) && this.checkActiveState(element.data) !== 'paused');
          break;
        case 'activity':
          addToList(this.calculateNextExecution(element.data.content).isSameOrAfter(moment().startOf('week')));
          break;
        case 'event':
          addToList(this.checkEventDate(element.data.content))
          break;
        case 'voting':
          addToList(this.checkItemPublication(element.data) && !element?.data?.content?.finished);
          break;
        case 'voucher':
          addToList(this.checkItemPublication(element.data) && moment(element.data.content.expirationDate).isSameOrAfter(today));
          break;
        default:
          addToList(this.checkActiveState(element.data) !== 'paused');
          break;
      }
    }
    return archive.length ? { live, archive } : live;
  }

  checkItemPublication(item): boolean {
    if (!item?.isActive) {
      return true;
    }
    if (item?.isActive && !item?.publishStart && !item?.publishEnd) {
      return true;
    }
    if (item?.publishStart) {
      const today = new Date();
      const publishStartDate = new Date(item.publishStart);
      if (!item?.publishEnd) {
        return true;
      } else {
        const publishEndDate = new Date(item.publishEnd);
        if (item?.publishEndTime) {
          publishEndDate.setHours(item?.publishEndTime.split(':')[0]);
          publishEndDate.setMinutes(item?.publishEndTime.split(':')[1]);
        }
        if ((publishStartDate.getTime() > today.getTime()) || (publishEndDate.getTime() > today.getTime())) {
          return true;
        }
      }
    }
    if (item?.publishEnd) {
      const today = new Date();
      const publishEndDate = new Date(item.publishEnd);
      if (item?.publishEndTime) {
        publishEndDate.setHours(item?.publishEndTime.split(':')[0]);
        publishEndDate.setMinutes(item?.publishEndTime.split(':')[1]);
      }
      if (publishEndDate.getTime() > today.getTime()) {
        return true;
      }
    }
    return false;
  }

  private checkEventDate(content): boolean {
    if (!content) {
      return false;
    }
    if (content?.repeating) {
      let dates = this.getNextExecutionDateRepeating(content);
      const today = new Date();
      today.setHours(0,0,0,0);
      dates = dates.filter(date => date.getTime() >= today.getTime());
      return !!dates?.length
    }
    const { validity, multipleDates, dates } = content;
    if (validity && !multipleDates) {
      return this.checkValidDate(validity?.start, validity?.end);
    }
    const eventDates = multipleDates || (Array.isArray(dates) ? dates : [dates]);
    if (!eventDates.length) {
      return true;
    }
    return eventDates.some(date => {
      if (date.daily === undefined) {
        return false;
      }
      const startDate = this.getDateUnix(date.startDate);
      const endDate = this.getDateUnix(date.endDate, true);
      return (!startDate && !endDate) || this.checkValidDate(startDate, endDate);
    });
  }

  getNextExecutionDateRepeating(event: any) {
    let endDate = !!event?.repetition?.endDate ? new Date(event.repetition.endDate) : null;
    if (!endDate) {
      endDate = new Date();
      endDate.setFullYear(endDate.getFullYear() + 1);
    }
    if (this.app?.detail?.publicationTimespan) {
      let d = new Date();
      if (this.app.detail.publicationTimespan.days) {
        d.setDate(d.getDate() + this.app.detail.publicationTimespan.days);
        endDate = d;
      } else if (this.app.detail.publicationTimespan.months != null) {
        d.setMonth(d.getMonth() + this.app.detail.publicationTimespan.months);
        endDate = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      }
    }
    endDate.setHours(23, 59, 59, 999);
    let date = new Date(event.repetition.startDate);
    const nth = this.getWeekdayOccurrence(date);
    const day = date.getDay();
    const dates = [];
    dates.push(new Date(date));
    if (event.repetition.repetitionSize === 'day') {
      if (event.repetition.repetitionRhythm === '1') {
        date.setDate(date.getDate() + 1);
        while (date.getTime() < endDate.getTime()) {
          if (event.repetition.weekdays.includes(date.getDay().toString()) || event.repetition.weekdays.includes(date)) {
            dates.push(new Date(date));
          }
          date.setDate(date.getDate() + 1);
        }
      } else {
        date.setDate(date.getDate() + parseInt(event.repetition.repetitionRhythm));
        while (date.getTime() < endDate.getTime()) {
          dates.push(new Date(date));
          date.setDate(date.getDate() + parseInt(event.repetition.repetitionRhythm));
        }
      }
    } else if (event.repetition.repetitionSize === 'week') {
      const date = this.getMonday(event.repetition.startDate);
      while (date.getTime() < endDate.getTime()) {
        for (let i = 0; i < 7; i++) {
          if (date.getTime() < new Date(event.repetition.startDate).getTime()) {
            date.setDate(date.getDate() + 1);
            continue;
          }
          if (event.repetition.weekdays.includes(date.getDay().toString()) || event.repetition.weekdays.includes(date.getDay())) {
            dates.push(new Date(date));
          }
          date.setDate(date.getDate() + 1);
          if (i === 6) {
            date.setDate(date.getDate() + ((parseInt(event.repetition.repetitionRhythm) - 1) * 7));
          }
        }
      }
    } else if (event.repetition.repetitionSize === 'month') {
      if (event.repetition.repetitionLastDay) {
        while (date.getTime() < endDate.getTime()) {
          date.setDate(1);
          date.setMonth(date.getMonth() + parseInt(event.repetition.repetitionRhythm, 10) + 1);
          date.setDate(0);
          dates.push(new Date(date));
        }
      } else if (event.repetition.repetitionExact) {
        while (date.getTime() < endDate.getTime()) {
          dates.push(new Date(date));
          date.setDate(date.getDate() + this.getDaysInMonth(date));
        }
      } else {
        while (date.getTime() < endDate.getTime()) {
          dates.push(new Date(date));
          date = this.getNthWeekdayOfNextMonth(date, day, nth);
        }
      }
    } else if (event.repetition.repetitionSize === 'year') {
      if (event.repetition.repetitionExact) {
        while (date.getTime() < endDate.getTime()) {
          dates.push(new Date(date));
          date = new Date(date.getFullYear() + 1, date.getMonth(), date.getDate())
        }
      } else {
        while (date.getTime() < endDate.getTime()) {
          dates.push(new Date(date));
          for (let i = 0; i < 12; i++) {
            date = this.getNthWeekdayOfNextMonth(date, day, nth);
          }
        }
      }
    }
    return dates;
  }

  getMonday(date) {
    const currentDate = new Date(date);
    const dayOfWeek = currentDate.getDay();
    const diff = (dayOfWeek === 0) ? -6 : 1 - dayOfWeek;
    currentDate.setDate(currentDate.getDate() + diff);
    currentDate.setHours(0, 0, 0, 0);
    return currentDate;
  }

  getDaysInMonth(date) {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  }

  getWeekdayOccurrence(date) {
    const currentDate = new Date(date);
    currentDate.setDate(1);
    const firstOccurrence = 1 + ((date.getDay() - currentDate.getDay() + 7) % 7);
    return Math.floor((date.getDate() - firstOccurrence) / 7) + 1;
  }

  getNthWeekdayOfNextMonth(currentDate, targetWeekday, targetOccurrence) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstOfNextMonth = new Date(year, month + 1, 1);
    const firstWeekday = firstOfNextMonth.getDay();
    const dayDiff = (targetWeekday - firstWeekday + 7) % 7;
    const firstTargetWeekday = 1 + dayDiff;
    const dayOfMonth = firstTargetWeekday + (targetOccurrence - 1) * 7;
    return new Date(year, month + 1, dayOfMonth);
  }

  private checkValidDate(startDate, endDate) {
    // (contentEntry.detail.validity && ((contentEntry.detail.validity.end && this.today > contentEntry.detail.validity.end))));
    return !((endDate && this.getNulledDate() > endDate) || (!endDate && this.getNulledDate() > startDate));
  }

  private getNulledDate(): number {
    return new Date().setHours(0, 0, 0, 0) / 1000;
  }

  private getDateUnix(dateString, isEndDate = false) {
    const res = this.getDate(dateString, isEndDate);
    return !!res ? res.getTime() / 1000 : null;
  }

  private getDate(dateString: string, isEndDate = false): Date | null {
    if (!dateString) {
      return null;
    }
    let res: Date;
    if (/\d?\d\.\d?\d\.(\d{2}|\d{4})/.test(dateString)) {
      const [day, month, year] = dateString.split('.').map(Number);
      res = new Date(year, month - 1, day); // Month is 0-based
    } else {
      res = new Date(dateString);
    }
    if (isEndDate) {
      res.setHours(23, 59, 59);
    }
    return res;
  }

  public calculateNextExecution(content: any): any {
    content = content?.content || content;
    const todayMom = moment();
    if (content.repeating) {
      const mom = moment(content.date || undefined);
      switch (content.rhythm) {
        case 'weekly':
          while (content.weekdays.indexOf(todayMom.weekday()) === -1) {
            todayMom.add(1, 'day');
          }
          break;
        case 'monthly':
          if (mom.date() < todayMom.date()) {
            todayMom.add(1, 'month');
          }
          todayMom.date(mom.date());
          break;
        case 'yearly':
          if (mom.isBefore(todayMom, 'day')) {
            todayMom.add(1, 'year');
          }
          todayMom.month(mom.month()).date(mom.date());
      }
      return todayMom;
    }
    return moment(content.date);
  }

  checkActiveState(element) {
    if (!element.published) { return 'disabled'; }
    if (element?.content?.finished) { return 'finished'; }

    const now = new Date();
    const publishEndTimeValid = this.isPublishEndTimeValid(element.publishEnd, element.publishEndTime, now);

    return (!DynamicContentActionComponent.weekdaysValid(element.weekdaysInterval) || !publishEndTimeValid) ? 'paused' : 'active';
  }

  private sameDay(d1, d2) {
    return d1 && d2 && d1.toDateString() === d2.toDateString();
  }

  private isPublishEndTimeValid(publishEnd, publishEndTime, now) {
    const endDate = this.cleanUpDate(publishEnd);
    if (!endDate || endDate >= now) {
      return true;
    } else if (this.sameDay(now, endDate) && publishEndTime) {
      const [hours, minutes] = publishEndTime.split(':').map(Number);
      return hours > now.getHours() || (hours === now.getHours() && minutes >= now.getMinutes());
    }
    return false;
  }

  private cleanUpDate(d) {
    return typeof d === 'string' && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}/gm.test(d) ? new Date(d).getTime() / 1000 : d;
  }

  private async pushToTree(item, level): Promise<any> {
    level += 1;
    item['translation'] = [];
    item['level'] = level;
    const children = [];
    if (item.translationEntries && item.translationEntries.length) {
      for (const id of item.translationEntries) {
        if (this.translations[id]) {
          item['translation'].push(this.translations[id].lang);
        }
      }
    }
    if (item.children && item.children.length) {
      for (const id of item.children) {
        if (this.children[id]) {
          const entry = _.cloneDeep(this.children[id]);
          const subEntry = await this.pushToTree(entry, level);
          subEntry.data.isActive = this.checkValid(subEntry.data);
          subEntry.data.parent = item._id;
          children.push(subEntry);
        } else {
          console.warn(id);
        }
      }
    }
    children.sort((obj1, obj2) => {
      return item.children.indexOf(obj1.data._id) - item.children.indexOf(obj2.data._id);
    });
    item.children = children.map(el => el.data);
    return { data: item, children: children };
  }

  private createPageActions(): void {
    this.showHistory = !!(this.messengerService.$superadminPrivileges.getValue() || []).find(p => p.value === 'dynamic-content-historys');
    this.pageActions = [
      new PageAction({
        icon: 'language', title: '', translationString: 'general.queuePriority', hidden: !this.priv.checkIfPrivilege('write', 'packages'),
        callback: () => this.que.triggerPriorities(this.packageIds)
      }), new PageAction({ icon: 'history', title: '', translationString: 'general.showHistory', hidden: !this.showHistory, callback: () => this.openHistory() })
    ];
  }

  private openHistory() {
    this.router.navigate([this.packageIds[0], 'history', this.dynamicContentEntrances[this.tabIndex]?._id], { relativeTo: this.route.parent });
  }

  private checkCurrentPipe(entranceId: string) {
    this.showInitPipeData = false;
    this.showReloadPipeData = false;
    this.currentPipe = this.pipes.find(p => (p?.modules || []).find(m => m?.data?.entranceId === entranceId));
    this.showReloadPipeData = this.currentPipe;
    this.showInitPipeData = !!(this.currentPipe?.modules || []).find(m => m.group === 'Request' && m.module === 'Upload');
  }

  private async initPipeImport(OwnerOrganisationID) {
    this.pipes = (await this.pipeImportService.find(0, 100, {
      OwnerOrganisationID: OwnerOrganisationID, executableByUser: true, toDelete: false, 'modules.group': 'Target', 'modules.module': 'DynamicContent'
    })).data;
  }

  getApp(entrance: any) {
    return this.apps.find(a => a.dynamicContentEntranceId === entrance._id);
  }
}
